import {Component, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {
  clinicalFingerPrintRoute,
  collectionsRoute,
  denialsRoute,
  emPageRoute,
  npvRoute,
  overviewPageRoute,
  procedureSummaryRoute,
  wRVUsRoute,
} from '../shared/routes';
import {canHide, getAppConfigValue} from '../shared/helpers';
import {NgRedux, select} from '@angular-redux/store';
import {IAppState, INITIAL_STATE} from '../store/IAppState';
import {combineLatest, Observable, Subscription, timer} from 'rxjs';
import {
  ApplicationConfigurationSetting,
  BatchExportReportDetailsResponse,
  FeatureToggleSetting,
  UserProfile
} from '../shared/models';
import {FeatureToggleEntries} from '../shared/feature-toggle-settings-enum';
import {AppConfigEntries} from '../shared/app-config-settings-enum';
import {AnalyticsService, AnalyticsServiceToken} from '../analytics/analytics.service';
import * as _ from 'lodash';
import {batchExportReportDetailsChangedTo, newProviderExportsChangedTo} from '../store/actions';
import {Event, NavigationEnd, Router} from '@angular/router';
import {BatchExportServiceToken, HttpBatchExportService} from '../batch-export/services/batch-export.service';
import {BatchExportStatus} from '../shared/enums';
import {filter, skipWhile, take} from 'rxjs/operators';
import {MatDialog} from '@angular/material/dialog';
import {ExportedReportsDialogComponent} from '../batch-export/exported-reports-dialog/exported-reports-dialog.component';

@Component({
  selector: 'app-metric-nav',
  templateUrl: './metric-nav.component.html',
  styleUrls: ['./metric-nav.component.scss'],
})
export class MetricNavComponent implements OnInit, OnDestroy {

  // noinspection JSUnusedLocalSymbols
  routes: any[];

  @select(['ontologyLoaded'])
  private readonly ontologyLoaded$: Observable<boolean>;

  @select(['data', 'featureToggleSettings'])
  private readonly featureToggleSettings$: Observable<FeatureToggleSetting[]>;

  @select(['data', 'applicationConfigurationSettings'])
  private readonly applicationConfigurationSettings$: Observable<ApplicationConfigurationSetting[]>;

  @select(['display', 'isProviderSelected'])
  private readonly isProviderSelected$: Observable<boolean>;

  @select(['display', 'filterBannerEdit'])
  readonly filterBannerEdit$: Observable<boolean>;

  @select(['display', 'newProviderExports'])
  readonly newProviderExports$: Observable<number>;

  @select(['data', 'userProfile'])
  readonly userProfile$: Observable<UserProfile>;

  @select(['filters', 'memberKey'])
  readonly memberKey$: Observable<number>;

  // showMoreApp = false;
  applicationSettings: ApplicationConfigurationSetting[];
  featureToggleReady = false;
  // showMyReports = false;
  subscription: Subscription;
  pollingSubscription: Subscription;
  canHideRecoveryDashboard = true;
  pollingInterval = 30; // in seconds so the default is polling every half minute
  userProfile: UserProfile;
  memberKey: number;
  private featureSettings: FeatureToggleSetting[] = [];
  private routerSubscription: Subscription;


  constructor(private readonly ngRedux: NgRedux<IAppState>,
              private router: Router,
              public dialog: MatDialog,
              @Inject(BatchExportServiceToken) private batchExportService: HttpBatchExportService,
              @Inject(AnalyticsServiceToken) private readonly analyticsService: AnalyticsService) {
  }

  ngOnInit() {
    this.subscription = combineLatest([this.userProfile$, this.memberKey$, this.applicationConfigurationSettings$,
      this.featureToggleSettings$, this.isProviderSelected$]
    ).pipe(skipWhile(([userProfile, memberKey, applicationSettings, featureSettings, isProviderSelected]:
                        [UserProfile, number, ApplicationConfigurationSetting[], FeatureToggleSetting[], boolean]) =>
      memberKey < 0 || !userProfile.email || !applicationSettings?.length || !featureSettings?.length))
      .subscribe(([userProfile, memberKey, applicationSettings,
                    featureSettings, isProviderSelected]) => {
        this.userProfile = userProfile;
        this.memberKey = memberKey;
        this.applicationSettings = applicationSettings;
        this.featureSettings = featureSettings;
        this.featureToggleReady = !_.isEqual(INITIAL_STATE.data.featureToggleSettings, this.featureSettings);
        this.canHideRecoveryDashboard = canHide(FeatureToggleEntries.RECOVERY_DASHBOARD, this.featureSettings, this.userProfile);
        const polling = +getAppConfigValue(AppConfigEntries.POLLING_INTERVAL, this.applicationSettings);
        if (polling) {
          this.pollingInterval = polling;
        }
        // Polling every this.pollingInterval seconds to fetch the latest count of reports that are unread
          this.pollingSubscription?.unsubscribe();
          this.pollingSubscription = combineLatest([
            this.ontologyLoaded$.pipe(filter((ontologyLoaded: boolean) => ontologyLoaded)),
            timer(0, this.pollingInterval * 1000)
          ]).subscribe(() => this.fetchBatchExportDetails());
        this.setRoutes(this.featureSettings);
        this.routerSubscription = this.router.events.subscribe((event: Event) => {
          if (event instanceof NavigationEnd) {
            this.setRoutes(this.featureSettings);
          }
        });
      });
  }

  fetchBatchExportDetails(): void {
    this.batchExportService.getBatchExportReportDetailsByUser(this.memberKey)
      .pipe(take(1))
      .subscribe((rsp: BatchExportReportDetailsResponse) => {
        this.ngRedux.dispatch(batchExportReportDetailsChangedTo(rsp.batchExportReportDetails));
        const completedUnreadReports = rsp.batchExportReportDetails.filter(
          r => r.reportStatus === BatchExportStatus.COMPLETED && !r.readStatus);
        this.ngRedux.dispatch(newProviderExportsChangedTo(completedUnreadReports.length));
      }, (e) => {
        console.log('ERROR fetching exported files: ' + e);
      });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.pollingSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
  }

  setRoutes(featureSettings: FeatureToggleSetting[]): void {

    this.routes = [
      {
        href: overviewPageRoute, description: 'Overview',
        hide: false
      },
      {href: wRVUsRoute, description: 'wRVUs'},
      {
        href: clinicalFingerPrintRoute, description: 'Clinical Fingerprint',
        hide: false
      },
      {
        href: procedureSummaryRoute, description: 'Procedure Summary',
        hide: !this.featureToggleReady || canHide(FeatureToggleEntries.PROCEDURE_SUMMARY,
          featureSettings, this.ngRedux.getState().data.userProfile)
      },
      {href: npvRoute, description: 'New Patient Visits'},
      {href: emPageRoute, description: 'Evaluation & Management'},
      {
        href: collectionsRoute, description: 'Collections',
        hide: !this.ngRedux.getState().display.isRevenueMember
      },
      {
        href: denialsRoute, description: 'Denials',
        hide: !this.featureToggleReady || !this.ngRedux.getState().display.isRevenueMember
          || canHide(FeatureToggleEntries.DENIALS_METRIC, featureSettings, this.ngRedux.getState().data.userProfile)
      }
    ];
  }

  openAccessAndThroughput() {
    window.open(getAppConfigValue(AppConfigEntries.ACCESS_AND_THROUGHPUT, this.applicationSettings));
    this.analyticsService.handleGoogleAnalytics('MoreApps', 'More Apps Click', 'Access and Throughput');
  }

  openAccessAndThroughputFromDropdown() {
    window.open(getAppConfigValue(AppConfigEntries.ATT_REPORT, this.applicationSettings));
    this.analyticsService.handleGoogleAnalytics('MoreApps', 'More Apps Click', 'AT&T Reports');
  }

  openAppReport() {
    window.open(getAppConfigValue(AppConfigEntries.APP_REPORT, this.applicationSettings));
    this.analyticsService.handleGoogleAnalytics('AppReport', 'App Impact Report Click', 'App Impact Report');
  }

  openRecoveryDashboard() {
    window.open(getAppConfigValue(AppConfigEntries.RECOVERY_DASHBOARD, this.applicationSettings));
    this.analyticsService.handleGoogleAnalytics('MoreApps', 'More Apps Click', 'Recovery Dashboard');
  }

  openOppDashboard() {
    window.open(getAppConfigValue(AppConfigEntries.OPPDASHBOARD_WEBSITE, this.applicationSettings));
    this.analyticsService.handleGoogleAnalytics('MoreApps', 'More Apps Click', 'Opportunity Dashboard');
  }

  openClinicalActivity() {
    let url: string = getAppConfigValue(AppConfigEntries.CLINICAL_ACTIVITY_WEBSITE, this.applicationSettings);
    url = url.replace('@memberKey', this.memberKey + '');
    window.open(url);
    this.analyticsService.handleGoogleAnalytics('MoreApps', 'More Apps Click', 'Clinical Activity');
  }

  openMultiProviderExports(): void {
    this.dialog.open(ExportedReportsDialogComponent);
  }

  handleMyReportsGoogleAnalytics() {
      this.analyticsService.handleGoogleAnalytics('MyReports', 'My Reports Click', 'My Reports');
  }
}
